/* eslint-disable no-nested-ternary */
/* eslint-disable no-unused-vars */
import { Box, Grid, Typography, Card, MenuItem, Select, Skeleton, Fade, Paper, Avatar, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { dashboardApi } from '../../../api/dashboardApi';
import { useState, useCallback, useEffect, useRef } from 'react';
import useMounted from '../../../hooks/useMounted';
import useAuth from '../../../hooks/useAuth';
import Chart from 'react-apexcharts';
import numeral from 'numeral';
import StatsWidget from './StatsWidget';
import PrimeWidget from './PrimeWidget';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import { getTone, toneAlpha, grey, lightVeil, darkVeil, withAlpha, greyAlpha } from '../../../theme/tones';

const ChartLine = () => {
  const theme = useTheme();
  const chartOptions = {
    chart: {
      background: 'transparent',
      toolbar: {
        show: false
      },
      zoom: {
        enabled: false
      }
    },
    colors: [getTone('brandGreen').base],
    dataLabels: {
      enabled: false
    },
    grid: {
      show: false
    },
    stroke: {
      curve: 'smooth',
      width: 3
    },
    theme: {
      mode: theme.palette.mode
    },
    tooltip: {
      enabled: false
    },
    xaxis: {
      labels: {
        show: false
      },
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false
      }
    },
    yaxis: {
      show: false
    }
  };

  const chartSeries = [{ data: [29, 11, 22, 27, 13, 22, 37, 21, 44, 22, 30] }];

  return (
    <Chart
      options={chartOptions}
      series={chartSeries}
      type="line"
    />
  );
};

const AnimatedNumber = ({ value, decimals = 2, suffix = '' }) => {
  return (
    <CountUp
      end={value}
      decimals={decimals}
      decimal=","
      separator=" "
      duration={1.2}
      suffix={suffix}
    />
  );
};

const getCardStyles = (theme, type) => {
  const isDark = theme.palette.mode === 'dark';
  const baseText = isDark ? grey(50) : getTone('brand').deepest;
  const baseSubtext = isDark ? grey(400) : grey(500);

  const CARD_TONES = {
    green: 'emerald',
    blue: 'blue',
    gold: 'amber',
    orange: 'orange',
    purple: 'violet'
  };

  const accent = getTone(CARD_TONES[type] || 'blue').base;

  return {
    bg: isDark ? greyAlpha(800, 0.45) : lightVeil(0.8),
    border: isDark ? `1px solid ${lightVeil(0.08)}` : `1px solid ${darkVeil(0.06)}`,
    shadow: `0 4px 20px -2px ${darkVeil(isDark ? 0.3 : 0.03)}`,
    hoverShadow: isDark
      ? `0 12px 25px -4px ${darkVeil(0.5)}, 0 0 15px ${withAlpha(accent, 0.25)}`
      : `0 12px 25px -4px ${darkVeil(0.04)}, 0 0 12px ${withAlpha(accent, 0.15)}`,
    text: baseText,
    subtext: baseSubtext,
    accent,
    badgeBg: withAlpha(accent, isDark ? 0.12 : 0.06),
    badgeColor: accent,
    shapeBg: accent
  };
};

const toNum = (v) => Number(Array.isArray(v) ? (v[0] ?? 0) : v) || 0;

const getVariation = (cur, last) => {
  if (last > 0) return Number((((cur - last) / last) * 100).toFixed(1));
  if (cur > 0) return 100;
  return 0;
};

// HERO — métrique vedette (Mois en cours) pour la vue commerciale
const HeroStatCard = ({ loading, title, sales, lastSales, amount }) => {
  const cur = toNum(sales);
  const variation = getVariation(cur, toNum(lastSales));
  const up = variation >= 0;
  return (
    <Card
      component={motion.div}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '24px',
        p: { xs: 3, md: 4.5 },
        minHeight: 210,
        color: grey(0),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${getTone('blue').color} 0%, ${getTone('indigo').color} 55%, ${getTone('violet').color} 100%)`,
        boxShadow: `0 24px 50px -18px ${toneAlpha('indigo', 0.6)}`,
      }}
    >
      <Box sx={{ position: 'absolute', top: -80, right: -40, width: 260, height: 260, borderRadius: '50%', background: `radial-gradient(circle, ${lightVeil(0.18)} 0%, ${lightVeil(0)} 70%)` }} />
      <Box sx={{ position: 'absolute', bottom: -110, right: 150, width: 220, height: 220, borderRadius: '50%', background: `radial-gradient(circle, ${lightVeil(0.10)} 0%, ${lightVeil(0)} 70%)` }} />
      <Box sx={{ position: 'absolute', top: 24, right: 24, width: 60, height: 60, borderRadius: '20px', display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center', backgroundColor: lightVeil(0.16), backdropFilter: 'blur(6px)', border: `1px solid ${lightVeil(0.25)}` }}>
        <CalendarMonthRoundedIcon sx={{ fontSize: '2rem' }} />
      </Box>

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Typography sx={{ fontWeight: 800, letterSpacing: '1.5px', fontSize: '0.72rem', textTransform: 'uppercase', opacity: 0.85, mb: 1 }}>
          {title}
        </Typography>
        {!loading ? (
          <Typography sx={{ fontWeight: 900, lineHeight: 1, fontSize: { xs: '2.4rem', md: '3.4rem' }, letterSpacing: '-1px' }}>
            <AnimatedNumber value={toNum(amount)} decimals={2} suffix=" €" />
          </Typography>
        ) : (
          <Skeleton variant="text" width={260} height={56} animation="wave" sx={{ bgcolor: lightVeil(0.2) }} />
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mt: 2 }}>
          {!loading ? (
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', opacity: 0.95 }}>
              <AnimatedNumber value={cur} /> vente{cur > 1 ? 's' : ''} ce mois
            </Typography>
          ) : (
            <Skeleton variant="text" width={130} height={26} animation="wave" sx={{ bgcolor: lightVeil(0.2) }} />
          )}
          {!loading && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.3, py: 0.5, borderRadius: '10px', backgroundColor: lightVeil(0.18), border: `1px solid ${lightVeil(0.25)}`, fontWeight: 800, fontSize: '0.82rem' }}>
              {up ? '▲' : '▼'} {variation > 0 ? '+' : ''}{variation}%
              <Box component="span" sx={{ fontWeight: 500, opacity: 0.82, ml: 0.5 }}>vs mois dernier</Box>
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
};

// Tuile secondaire compacte
const MiniStat = ({ loading, title, sales, lastSales, amount, accent }) => {
  const cur = toNum(sales);
  const variation = getVariation(cur, toNum(lastSales));
  const up = variation >= 0;
  const flat = variation === 0;
  const trendColor = flat ? getTone('neutral').base : (up ? getTone('emerald').color : getTone('rose').color);
  const trendBg = flat ? toneAlpha('neutral', 0.1) : (up ? toneAlpha('emerald', 0.1) : toneAlpha('rose', 0.1));
  return (
    <Card
      component={motion.div}
      whileHover={{ y: -4 }}
      sx={{
        p: 2.5,
        height: '100%',
        borderRadius: '18px',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderLeft: `4px solid ${accent}`,
        boxShadow: (theme) => (theme.palette.mode === 'light' ? `0 6px 18px -10px ${darkVeil(0.12)}` : `0 6px 18px -10px ${darkVeil(0.5)}`),
        transition: 'box-shadow 0.25s ease',
      }}
    >
      <Typography sx={{ fontWeight: 700, letterSpacing: '0.8px', fontSize: '0.66rem', textTransform: 'uppercase', color: 'text.secondary' }}>
        {title}
      </Typography>
      {!loading ? (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 0.5 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.7rem', color: 'text.primary', lineHeight: 1 }}>
            <AnimatedNumber value={cur} />
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            vente{cur > 1 ? 's' : ''}
          </Typography>
        </Box>
      ) : (
        <Skeleton variant="text" width={110} height={34} animation="wave" />
      )}
      {!loading ? (
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: accent, mt: 0.25 }}>
          <AnimatedNumber value={toNum(amount)} decimals={2} suffix=" €" />
        </Typography>
      ) : (
        <Skeleton variant="text" width={80} height={22} animation="wave" />
      )}
      {!loading && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, mt: 1, px: 0.9, py: 0.4, borderRadius: '8px', backgroundColor: trendBg, color: trendColor, fontWeight: 800, fontSize: '0.72rem' }}>
          {flat ? '■' : (up ? '▲' : '▼')} {variation > 0 ? '+' : ''}{variation}%
        </Box>
      )}
    </Card>
  );
};

const FinanceOverview = (year) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  const greenStyles = getCardStyles(theme, 'green');
  const blueStyles = getCardStyles(theme, 'blue');
  const goldStyles = getCardStyles(theme, 'gold');
  const purpleStyles = getCardStyles(theme, 'purple');
  const orangeStyles = getCardStyles(theme, 'orange');

  const [finishedTraining, setFinishedTraining] = useState([]);
  const [remainsTraining, setRemainsTraining] = useState([]);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [transformationRate, setTransformationRate] = useState(0);
  const mounted = useMounted();
  const { user } = useAuth();
  const [reload, setReload] = useState(1);
  const [todayInscriptions, setTodayInscriptions] = useState([]);
  const [inscriptionCardHovered, setInscriptionCardHovered] = useState(false);
  const inscriptionCardRef = useRef(null);
  const [adminInscriptionCardHovered, setAdminInscriptionCardHovered] = useState(false);
  const adminInscriptionCardRef = useRef(null);
  const [apiDebugInfo, setApiDebugInfo] = useState({ url: '', status: '', error: '' });

  useEffect(() => {
    const getOverview = async () => {
      try {
        const donnees = await dashboardApi.getOverview(localStorage.getItem('accessToken'), year.selectedYear);

        if (mounted.current) {
          if (donnees) {
            setData({
              customer: {
                currentYearCA: donnees.customersCurrentYearCA ? donnees.customersCurrentYearCA.map((item) => item.currentYearCA) : [],
                currentMonthCA: donnees.customersCurrentMonthCA ? donnees.customersCurrentMonthCA.map((item) => item.currentMonthCA) : [],
                currentWeekCA: donnees.customersCurrentWeekCA ? donnees.customersCurrentWeekCA.map((item) => item.currentWeekCA) : [],
                todayCA: donnees.customersTodayCA ? donnees.customersTodayCA.map((item) => item.todayCA) : [],
                lastYearCA: donnees.customersLastYearCA ? donnees.customersLastYearCA.map((item) => item.lastYearCA) : [],
                lastMonthCA: donnees.customersLastMonthCA ? donnees.customersLastMonthCA.map((item) => item.lastMonthCA) : [],
                lastWeekCA: donnees.customersLastWeekCA ? donnees.customersLastWeekCA.map((item) => item.lastWeekCA) : [],
                yesterdayCA: donnees.customersYesterdayCA ? donnees.customersYesterdayCA.map((item) => item.yesterdayCA) : [],
                currentYearSubscribers: donnees.customersCurrentYearSubscribers ? donnees.customersCurrentYearSubscribers.map((item) => item.currentYearSubscribers) : [],
                currentMonthSubscribers: donnees.customersCurrentMonthSubscribers ? donnees.customersCurrentMonthSubscribers.map((item) => item.currentMonthSubscribers) : [],
                currentWeekSubscribers: donnees.customersCurrentWeekSubscribers ? donnees.customersCurrentWeekSubscribers.map((item) => item.currentWeekSubscribers) : [],
                todaySubscribers: donnees.customersTodaySubscribers ? donnees.customersTodaySubscribers.map((item) => item.todaySubscribers) : [],
                lastYearSubscribers: donnees.customersLastYearSubscribers ? donnees.customersLastYearSubscribers.map((item) => item.lastYearSubscribers) : [],
                lastMonthSubscribers: donnees.customersLastMonthSubscribers ? donnees.customersLastMonthSubscribers.map((item) => item.lastMonthSubscribers) : [],
                lastWeekSubscribers: donnees.customersLastWeekSubscribers ? donnees.customersLastWeekSubscribers.map((item) => item.lastWeekSubscribers) : [],
                yesterdaySubscribers: donnees.customersYesterdaySubscribers ? donnees.customersYesterdaySubscribers.map((item) => item.yesterdaySubscribers) : [],
                currentMonthPalier: donnees.currentMonthPalier,
                lastMonthPalier: donnees.lastMonthPalier,
                nextPalier: donnees.nextPalier,
              },
              sales: {
                actualYearReals: Number(donnees?.numberofyesterdayReals?.[0]?.yesterdayReals ?? 0),
                lastYearReals: user.access_level === 0 ? Number(donnees?.numberoftodayReals?.[0]?.todayReals ?? 0) : '',
                actualYear: Number(donnees?.souscriptionsNumberOfDay?.[0]?.day1 ?? 0),
                amountOfToday: Number(donnees?.amountOfToday?.[0]?.today ?? 0),
              },
              profit: {
                actualYearReals: donnees.souscriptionsNumberOfWeekReals ? donnees.souscriptionsNumberOfWeekReals.map((item) => item.lastweekReals) : [],
                actualYear: donnees.souscriptionsNumberOfWeek ? donnees.souscriptionsNumberOfWeek.map((item) => item.lastweek) : [],
                potential: donnees.potential,
                lastYear: 10
              },
              cost: {
                actualYear: donnees.countCustomers ? donnees.countCustomers.map((item) => item.countcustomerReal) : [],
                actualYearBrut: donnees.countCustomers ? donnees.countCustomers.map((item) => item.countcustomerBrut) : [],
                lastYear: donnees.countCustomers ? donnees.countCustomers.map((item) => item.countcustomer) : [],
              },
              gap: {
                today: Number(donnees?.numberoftodayRealsInscrit?.[0]?.todayRealsInscrit ?? 0),
                yesterday: Number(donnees?.numberofyesterdayRealsInscrit?.[0]?.yesterdayRealsInscrit ?? 0),
                total: Number(donnees?.totalShifted ?? 0),
                totalFinishedShifted: Number(donnees?.totalFinishedShifted ?? 0),
                differenceToday: user.access_level === 0 ? Number(donnees?.shiftedTodayInscritD?.[0]?.day_today_inscritd ?? 0) : '',
                differenceYesterday: user.access_level === 0 ? Number(donnees?.shiftedYesterdayInscritD?.[0]?.day_yesterday_inscritd ?? 0) : '',
              },
            });
            setFinishedTraining(donnees.finishedTraining);
            setRemainsTraining(donnees.remainsTraining);
            setTransformationRate(donnees.transformationRate);
          }
          setLoading(false);
        }
      } catch (err) {
        console.error(err);
        if (mounted.current) {
          setLoading(false);
        }
      }
    };
    getOverview();
  }, [mounted, year, reload]);

  useEffect(() => {
    const interval = setInterval(() => setReload(prevReload => prevReload + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchTodayInscriptions = async () => {
      const apiUrl = process.env.REACT_APP_API_URL || 'undefined';
      const url = `${apiUrl}/api/dashboard/today-inscriptions`;
      try {
        setApiDebugInfo(prev => ({ ...prev, url }));
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`
          }
        });
        setApiDebugInfo(prev => ({ ...prev, status: response.status }));
        if (response.ok) {
          const json = await response.json();
          if (json.data && json.data.inscriptions) {
            setTodayInscriptions(json.data.inscriptions);
            setApiDebugInfo(prev => ({ ...prev, error: '' }));
          } else {
            setTodayInscriptions([]);
            setApiDebugInfo(prev => ({ ...prev, error: 'Structure de réponse invalide' }));
          }
        } else {
          const text = await response.text();
          setApiDebugInfo(prev => ({ ...prev, error: `HTTP ${response.status}: ${text.substring(0, 100)}` }));
          setTodayInscriptions([]);
        }
      } catch (err) {
        setApiDebugInfo(prev => ({ ...prev, error: err.message }));
        setTodayInscriptions([]);
        console.error('Erreur chargement inscriptions du jour:', err);
      }
    };
    fetchTodayInscriptions();
    const inscriptionInterval = setInterval(fetchTodayInscriptions, 60000);
    return () => clearInterval(inscriptionInterval);
  }, []);

  const netCAVal = data.cost?.actualYear?.[0] ? Number(data.cost.actualYear[0]) : 0;
  const brutCAVal = data.cost?.actualYearBrut?.[0] ? Number(data.cost.actualYearBrut[0]) : 0;
  const netBrutRatio = brutCAVal > 0 ? ((netCAVal / brutCAVal) * 100).toFixed(1) : '0.0';

  return (
    <>
      {/* CHIFFRE D'AFFAIRE POUR LE SUPER ADMIN */}
      {user.id_service === 0 && (
        <Grid
          container
          spacing={2}
          sx={{ mb: 4 }}
        >
          {/* Card 1: CA Aujourd'hui */}
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              component={motion.div}
              whileHover={{ y: -4 }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                p: 2,
                height: '100%',
                backgroundColor: greenStyles.bg,
                border: greenStyles.border,
                borderRadius: '16px',
                boxShadow: greenStyles.shadow,
                position: 'relative',
                overflow: 'visible',
                transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                minHeight: '135px',
                '&:hover': {
                  boxShadow: greenStyles.hoverShadow,
                  borderColor: withAlpha(greenStyles.accent, 0.4),
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography
                  color="text.secondary"
                  sx={{ fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.65rem', opacity: 0.85 }}
                >
                  CA Aujourd&apos;hui
                </Typography>
                <Box
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    width: 28,
                    height: 28,
                    backgroundColor: greenStyles.badgeBg,
                    borderRadius: '8px',
                    color: greenStyles.badgeColor,
                    flexShrink: 0,
                  }}
                >
                  {!loading ? (
                    <img src="ca_jour.svg" alt="" height={16} width={16} style={{ filter: isDark ? 'brightness(1.2)' : 'none' }} />
                  ) : (
                    <Skeleton variant="circular" width={16} height={16} animation="wave" />
                  )}
                </Box>
              </Box>

              {!loading ? (
                <Typography
                  sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.4rem', letterSpacing: '-0.3px', my: 0.5, whiteSpace: 'nowrap' }}
                >
                  <AnimatedNumber value={data.sales.lastYearReals} suffix=" €" />
                </Typography>
              ) : (
                <Skeleton variant="text" width={120} height={32} animation="wave" sx={{ my: 0.5 }} />
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  pt: 1,
                  mt: 'auto',
                  borderTop: `1px dashed ${isDark ? lightVeil(0.08) : darkVeil(0.06)}`,
                  width: '100%'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Hier</Typography>
                  {!loading ? (
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.primary' }}>
                      <AnimatedNumber value={data.sales.actualYearReals} suffix=" €" />
                    </Typography>
                  ) : (
                    <Skeleton variant="text" width={50} height={14} animation="wave" />
                  )}
                </Box>
                <Box sx={{ height: 16 }} />
              </Box>
            </Card>
          </Grid>

          {/* Card 2: Inscriptions Aujourd'hui */}
          <Grid
            item
            xs={12}
            sm={6}
            md={2.4}
            ref={inscriptionCardRef}
            onMouseEnter={() => setInscriptionCardHovered(true)}
            onMouseLeave={() => setInscriptionCardHovered(false)}
            sx={{ position: 'relative', overflow: 'visible' }}
          >
            <Card
              component={motion.div}
              whileHover={{ y: -4 }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                p: 2,
                height: '100%',
                backgroundColor: blueStyles.bg,
                border: blueStyles.border,
                borderRadius: '16px',
                boxShadow: inscriptionCardHovered ? blueStyles.hoverShadow : blueStyles.shadow,
                position: 'relative',
                overflow: 'visible',
                transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                minHeight: '135px',
                zIndex: inscriptionCardHovered ? 100 : 1,
                '&:hover': {
                  borderColor: withAlpha(blueStyles.accent, 0.4),
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography
                  color="text.secondary"
                  sx={{ fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.65rem', opacity: 0.85 }}
                >
                  Inscriptions Auj.
                </Typography>
                <Box
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    width: 28,
                    height: 28,
                    backgroundColor: blueStyles.badgeBg,
                    borderRadius: '8px',
                    color: blueStyles.badgeColor,
                    flexShrink: 0,
                  }}
                >
                  {!loading ? (
                    <img src="inscription.svg" alt="" height={16} width={16} style={{ filter: isDark ? 'brightness(1.2)' : 'none' }} />
                  ) : (
                    <Skeleton variant="circular" width={16} height={16} animation="wave" />
                  )}
                </Box>
              </Box>

              {!loading ? (
                <Typography
                  sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.4rem', letterSpacing: '-0.3px', my: 0.5, whiteSpace: 'nowrap' }}
                >
                  <AnimatedNumber value={data.gap.today} suffix=" €" />
                </Typography>
              ) : (
                <Skeleton variant="text" width={120} height={32} animation="wave" sx={{ my: 0.5 }} />
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  pt: 1,
                  mt: 'auto',
                  borderTop: `1px dashed ${isDark ? lightVeil(0.08) : darkVeil(0.06)}`,
                  width: '100%'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Hier</Typography>
                  {!loading ? (
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.primary' }}>
                      <AnimatedNumber value={data.gap.yesterday} suffix=" €" />
                    </Typography>
                  ) : (
                    <Skeleton variant="text" width={50} height={14} animation="wave" />
                  )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Auj. décalés</Typography>
                  {!loading ? (
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.primary' }}>
                      <AnimatedNumber value={data.gap.differenceToday} suffix=" €" />
                    </Typography>
                  ) : (
                    <Skeleton variant="text" width={50} height={14} animation="wave" />
                  )}
                </Box>
              </Box>

              {/* POPUP INSCRIPTIONS DU JOUR */}
              <Fade in={inscriptionCardHovered} timeout={250}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    left: '0',
                    width: '420px',
                    maxHeight: '380px',
                    overflow: 'hidden',
                    backgroundColor: (theme) => theme.palette.mode === 'light' ? lightVeil(0.98) : toneAlpha('neutral', 0.98),
                    backdropFilter: 'blur(20px)',
                    borderRadius: '12px',
                    boxShadow: (theme) => theme.palette.mode === 'light' 
                      ? `0 16px 36px -12px ${toneAlpha('sky', 0.2)}, 0 8px 16px -8px ${darkVeil(0.08)}`
                      : `0 16px 40px -12px ${darkVeil(0.6)}, 0 0 0 1px ${lightVeil(0.08)}`,
                    border: (theme) => `1px solid ${theme.palette.mode === 'light' ? toneAlpha('sky', 0.1) : lightVeil(0.08)}`,
                    zIndex: 9999,
                    pointerEvents: inscriptionCardHovered ? 'auto' : 'none',
                    display: inscriptionCardHovered ? 'block' : 'none',
                  }}
                >
                  {/* Header du popup */}
                  <Box
                    sx={{
                      background: `linear-gradient(135deg, ${getTone('cyan').deep}, ${getTone('sky').base})`,
                      p: '10px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography
                      sx={{
                        color: grey(0),
                        fontWeight: 700,
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.8px',
                      }}
                    >
                      📋 Inscriptions d&apos;aujourd&apos;hui
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: lightVeil(0.2),
                        borderRadius: '8px',
                        px: 1.2,
                        py: '1px',
                      }}
                    >
                      <Typography sx={{ color: grey(0), fontSize: '11px', fontWeight: 700 }}>
                        {todayInscriptions.length}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Liste des inscriptions */}
                  <Box 
                    sx={{ 
                      maxHeight: '300px', 
                      overflowY: 'auto',
                      scrollbarWidth: 'thin',
                      '&::-webkit-scrollbar': {
                        width: '4px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: (theme) => theme.palette.mode === 'light' ? darkVeil(0.1) : lightVeil(0.15),
                        borderRadius: '2px',
                      },
                    }}
                  >
                    {todayInscriptions.length === 0 ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography sx={{ color: 'text.secondary', fontSize: '12px', fontWeight: 500 }}>
                          Aucune inscription aujourd&apos;hui
                        </Typography>
                      </Box>
                    ) : (
                      todayInscriptions.map((insc, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            p: '10px 14px',
                            borderBottom: idx < todayInscriptions.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                            '&:hover': { backgroundColor: (theme) => theme.palette.mode === 'light' ? grey(100) : toneAlpha('sky', 0.05) },
                            transition: 'background-color 0.15s ease',
                          }}
                        >
                          {/* Apprenant */}
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: '4px' }}>
                            <Avatar
                              sx={{
                                width: 24,
                                height: 24,
                                background: `linear-gradient(135deg, ${getTone('blue').deep}, ${getTone('sky').light})`,
                                mr: 1,
                                fontSize: '10px',
                                fontWeight: 700,
                                color: grey(0),
                              }}
                            >
                              {(insc.apprenant_prenom || '?').charAt(0).toUpperCase()}{(insc.apprenant_nom || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 700, fontSize: '12px', color: 'text.primary' }}>
                                {insc.apprenant_prenom} {insc.apprenant_nom}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Formation */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', ml: '34px', mb: '2px' }}>
                            <Typography sx={{ fontSize: '10px', color: 'text.primary', lineHeight: 1.2, fontWeight: 500 }}>
                              🎓 {insc.formation_nom || 'N/A'}
                            </Typography>
                          </Box>

                          {/* Session */}
                          <Box sx={{ display: 'flex', alignItems: 'center', ml: '34px' }}>
                            <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
                              📅 Session {insc.session_numero || 'N/A'}
                            </Typography>
                            {insc.prix > 0 && (
                              <Typography sx={{ ml: 'auto', fontSize: '11px', color: 'primary.main', fontWeight: 700 }}>
                                <AnimatedNumber value={insc.prix} suffix=" €" />
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))
                    )}
                  </Box>
                </Box>
              </Fade>
            </Card>
          </Grid>

          {/* Card 3: CA Net Total */}
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              component={motion.div}
              whileHover={{ y: -4 }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                p: 2,
                height: '100%',
                backgroundColor: goldStyles.bg,
                border: goldStyles.border,
                borderRadius: '16px',
                boxShadow: goldStyles.shadow,
                position: 'relative',
                overflow: 'visible',
                transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                minHeight: '135px',
                '&:hover': {
                  boxShadow: goldStyles.hoverShadow,
                  borderColor: withAlpha(goldStyles.accent, 0.4),
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography
                  color="text.secondary"
                  sx={{ fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.65rem', opacity: 0.85 }}
                >
                  CA Net Total {year.selectedYear}
                </Typography>
                <Box
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    width: 28,
                    height: 28,
                    backgroundColor: goldStyles.badgeBg,
                    borderRadius: '8px',
                    color: goldStyles.badgeColor,
                    flexShrink: 0,
                  }}
                >
                  {!loading ? (
                    <img src="ca_total.svg" alt="" height={16} width={16} style={{ filter: isDark ? 'brightness(1.2)' : 'none' }} />
                  ) : (
                    <Skeleton variant="circular" width={16} height={16} animation="wave" />
                  )}
                </Box>
              </Box>

              {!loading ? (
                <Typography
                  sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.4rem', letterSpacing: '-0.3px', my: 0.5, whiteSpace: 'nowrap' }}
                >
                  <AnimatedNumber value={data.cost.actualYear} suffix=" €" />
                </Typography>
              ) : (
                <Skeleton variant="text" width={120} height={32} animation="wave" sx={{ my: 0.5 }} />
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  pt: 1,
                  mt: 'auto',
                  borderTop: `1px dashed ${isDark ? lightVeil(0.08) : darkVeil(0.06)}`,
                  width: '100%'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>dont décalés</Typography>
                  {!loading ? (
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.primary' }}>
                      <AnimatedNumber value={data.gap.total} suffix=" €" />
                    </Typography>
                  ) : (
                    <Skeleton variant="text" width={50} height={14} animation="wave" />
                  )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Taux de transf.</Typography>
                  {!loading ? (
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.primary' }}>
                      <AnimatedNumber value={transformationRate} suffix=" %" />
                    </Typography>
                  ) : (
                    <Skeleton variant="text" width={50} height={14} animation="wave" />
                  )}
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Card 4: CA Brut Total */}
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              component={motion.div}
              whileHover={{ y: -4 }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                p: 2,
                height: '100%',
                backgroundColor: orangeStyles.bg,
                border: orangeStyles.border,
                borderRadius: '16px',
                boxShadow: orangeStyles.shadow,
                position: 'relative',
                overflow: 'visible',
                transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                minHeight: '135px',
                '&:hover': {
                  boxShadow: orangeStyles.hoverShadow,
                  borderColor: withAlpha(orangeStyles.accent, 0.4),
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography
                  color="text.secondary"
                  sx={{ fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.65rem', opacity: 0.85 }}
                >
                  CA Brut Total {year.selectedYear}
                </Typography>
                <Box
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    width: 28,
                    height: 28,
                    backgroundColor: orangeStyles.badgeBg,
                    borderRadius: '8px',
                    color: orangeStyles.badgeColor,
                    flexShrink: 0,
                  }}
                >
                  {!loading ? (
                    <img src="ca_total.svg" alt="" height={16} width={16} style={{ filter: isDark ? 'brightness(1.2)' : 'none' }} />
                  ) : (
                    <Skeleton variant="circular" width={16} height={16} animation="wave" />
                  )}
                </Box>
              </Box>

              {!loading ? (
                <Typography
                  sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.4rem', letterSpacing: '-0.3px', my: 0.5, whiteSpace: 'nowrap' }}
                >
                  <AnimatedNumber value={data.cost.actualYearBrut} suffix=" €" />
                </Typography>
              ) : (
                <Skeleton variant="text" width={120} height={32} animation="wave" sx={{ my: 0.5 }} />
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  pt: 1,
                  mt: 'auto',
                  borderTop: `1px dashed ${isDark ? lightVeil(0.08) : darkVeil(0.06)}`,
                  width: '100%'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Rapport Net/Brut</Typography>
                  {!loading ? (
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.primary' }}>
                      {netBrutRatio} %
                    </Typography>
                  ) : (
                    <Skeleton variant="text" width={50} height={14} animation="wave" />
                  )}
                </Box>
                <Box sx={{ height: 16 }} />
              </Box>
            </Card>
          </Grid>

          {/* Card 5: Formations Terminées */}
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              component={motion.div}
              whileHover={{ y: -4 }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                p: 2,
                height: '100%',
                backgroundColor: purpleStyles.bg,
                border: purpleStyles.border,
                borderRadius: '16px',
                boxShadow: purpleStyles.shadow,
                position: 'relative',
                overflow: 'visible',
                transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                minHeight: '135px',
                '&:hover': {
                  boxShadow: purpleStyles.hoverShadow,
                  borderColor: withAlpha(purpleStyles.accent, 0.4),
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography
                  color="text.secondary"
                  sx={{ fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.65rem', opacity: 0.85 }}
                >
                  Formations Term. {year.selectedYear}
                </Typography>
                <Box
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    width: 28,
                    height: 28,
                    backgroundColor: purpleStyles.badgeBg,
                    borderRadius: '8px',
                    color: purpleStyles.badgeColor,
                    flexShrink: 0,
                  }}
                >
                  {!loading ? (
                    <img src="formation_terminée.svg" alt="" height={16} width={16} style={{ filter: isDark ? 'brightness(1.2)' : 'none' }} />
                  ) : (
                    <Skeleton variant="circular" width={16} height={16} animation="wave" />
                  )}
                </Box>
              </Box>

              {!loading ? (
                <Typography
                  sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.4rem', letterSpacing: '-0.3px', my: 0.5, whiteSpace: 'nowrap' }}
                >
                  <AnimatedNumber value={finishedTraining.finish} suffix=" €" />
                </Typography>
              ) : (
                <Skeleton variant="text" width={120} height={32} animation="wave" sx={{ my: 0.5 }} />
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  pt: 1,
                  mt: 'auto',
                  borderTop: `1px dashed ${isDark ? lightVeil(0.08) : darkVeil(0.06)}`,
                  width: '100%'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>dont décalés</Typography>
                  {!loading ? (
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.primary' }}>
                      <AnimatedNumber value={data.gap.totalFinishedShifted} suffix=" €" />
                    </Typography>
                  ) : (
                    <Skeleton variant="text" width={50} height={14} animation="wave" />
                  )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Potentiel</Typography>
                  {!loading ? (
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.primary' }}>
                      <AnimatedNumber value={data.profit.potential} suffix=" €" />
                    </Typography>
                  ) : (
                    <Skeleton variant="text" width={50} height={14} animation="wave" />
                  )}
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ADMIN & COMMERCIAL LAYOUTS */}
      {user.id_service !== 0 && (
        <>
          {user.id_service === 1 ? (
            <Grid
              container
              spacing={3}
              sx={{ mb: 1 }}
            >
              {/* Card 1: Inscrits d'aujourd'hui */}
              <Grid
                item
                md={3}
                sm={6}
                xs={12}
                ref={adminInscriptionCardRef}
                onMouseEnter={() => setAdminInscriptionCardHovered(true)}
                onMouseLeave={() => setAdminInscriptionCardHovered(false)}
                sx={{ position: 'relative', overflow: 'visible' }}
              >
                <Card
                  component={motion.div}
                  whileHover={{ y: -6, scale: 1.015 }}
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    p: 3,
                    height: '100%',
                    backgroundImage: blueStyles.bg,
                    border: blueStyles.border,
                    borderRadius: '16px',
                    boxShadow: adminInscriptionCardHovered 
                      ? (isDark ? `0 15px 35px -5px ${darkVeil(0.8)}, 0 8px 25px -5px ${toneAlpha('sky', 0.4)}` : `0 15px 30px -5px ${toneAlpha('sky', 0.3)}`)
                      : blueStyles.shadow,
                    overflow: 'visible',
                    position: 'relative',
                    minHeight: '200px',
                    cursor: 'default',
                    transition: 'box-shadow 0.3s ease',
                    zIndex: adminInscriptionCardHovered ? 100 : 1,
                  }}
                >
                  <Box sx={{ zIndex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                    <Typography
                      color={blueStyles.text}
                      variant="overline"
                      sx={{ fontWeight: 700, letterSpacing: '0.8px', opacity: 0.85 }}
                    >
                      Inscrits d&apos;aujourd&apos;hui
                    </Typography>
                    
                    {!loading ? (
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, color: blueStyles.text, mb: 1 }}
                      >
                        <AnimatedNumber value={data.sales.lastYearReals} suffix=" €" />
                      </Typography>
                    ) : (
                      <Skeleton variant="text" width={140} height={40} animation="wave" />
                    )}

                    <Typography
                      color={blueStyles.text}
                      variant="overline"
                      sx={{ fontWeight: 700, letterSpacing: '0.8px', opacity: 0.7 }}
                    >
                      Hier
                    </Typography>
                    {!loading ? (
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: blueStyles.text }}
                      >
                        <AnimatedNumber value={data.sales.actualYearReals} suffix=" €" />
                      </Typography>
                    ) : (
                      <Skeleton variant="text" width={100} height={24} animation="wave" />
                    )}
                  </Box>

                  <Box sx={{ zIndex: 1, width: 100, height: 50, opacity: 0.85, ml: 1 }}>
                    {!loading ? (
                      <ChartLine />
                    ) : (
                      <Skeleton variant="rectangular" width={100} height={50} animation="wave" />
                    )}
                  </Box>

                  {/* POPUP INSCRIPTIONS DU JOUR - ADMIN */}
                  <Fade in={adminInscriptionCardHovered} timeout={250}>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 'calc(100% + 12px)',
                        left: '0',
                        width: '440px',
                        maxHeight: '400px',
                        overflow: 'hidden',
                        backgroundColor: (theme) => theme.palette.mode === 'light' ? lightVeil(0.95) : toneAlpha('neutral', 0.95),
                        backdropFilter: 'blur(16px)',
                        borderRadius: '16px',
                        boxShadow: (theme) => theme.palette.mode === 'light' 
                          ? `0 20px 40px -15px ${toneAlpha('sky', 0.2)}, 0 10px 20px -10px ${darkVeil(0.08)}`
                          : `0 20px 45px -15px ${darkVeil(0.6)}, 0 0 0 1px ${lightVeil(0.08)}`,
                        border: (theme) => `1px solid ${theme.palette.mode === 'light' ? toneAlpha('sky', 0.1) : lightVeil(0.08)}`,
                        zIndex: 9999,
                        pointerEvents: adminInscriptionCardHovered ? 'auto' : 'none',
                        display: adminInscriptionCardHovered ? 'block' : 'none',
                      }}
                    >
                      {/* Header du popup */}
                      <Box
                        sx={{
                          background: `linear-gradient(135deg, ${getTone('cyan').deep}, ${getTone('sky').base})`,
                          p: '14px 18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography
                          sx={{
                            color: grey(0),
                            fontWeight: 700,
                            fontSize: '13px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.8px',
                          }}
                        >
                          📋 Inscriptions d&apos;aujourd&apos;hui
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: lightVeil(0.25),
                            borderRadius: '12px',
                            px: 1.5,
                            py: '2px',
                          }}
                        >
                          <Typography sx={{ color: grey(0), fontSize: '12px', fontWeight: 700 }}>
                            {todayInscriptions.length}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Liste des inscriptions */}
                      <Box 
                        sx={{ 
                          maxHeight: '330px', 
                          overflowY: 'auto',
                          scrollbarWidth: 'thin',
                          '&::-webkit-scrollbar': {
                            width: '6px',
                          },
                          '&::-webkit-scrollbar-track': {
                            background: 'transparent',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: (theme) => theme.palette.mode === 'light' ? darkVeil(0.1) : lightVeil(0.1),
                            borderRadius: '4px',
                          },
                        }}
                      >
                        {todayInscriptions.length === 0 ? (
                          <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography sx={{ color: 'text.secondary', fontSize: '13px', fontWeight: 500 }}>
                              Aucune inscription aujourd&apos;hui
                            </Typography>
                            <Box sx={{ mt: 2, p: 2, backgroundColor: (theme) => theme.palette.mode === 'light' ? grey(100) : lightVeil(0.03), borderRadius: '12px', textAlign: 'left', border: '1px dashed', borderColor: 'divider' }}>
                              <Typography sx={{ fontSize: '10px', color: 'text.secondary', fontFamily: 'monospace', fontWeight: 700, mb: '4px' }}>
                                ⚙️ Diagnostics de l&apos;API :
                              </Typography>
                              <Typography sx={{ fontSize: '9px', color: 'text.secondary', fontFamily: 'monospace', wordBreak: 'break-all', lineHeight: 1.4 }}>
                                <strong>URL:</strong> {apiDebugInfo.url}<br/>
                                <strong>Status:</strong> {apiDebugInfo.status || 'N/A'}<br/>
                                <strong>Erreur:</strong> {apiDebugInfo.error || 'Aucune erreur (liste vide)'}
                              </Typography>
                            </Box>
                          </Box>
                        ) : (
                          todayInscriptions.map((insc, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                p: '14px 18px',
                                borderBottom: idx < todayInscriptions.length - 1 ? '1px solid' : 'none',
                                borderColor: 'divider',
                                '&:hover': { backgroundColor: (theme) => theme.palette.mode === 'light' ? grey(100) : toneAlpha('sky', 0.08) },
                                transition: 'background-color 0.15s ease',
                              }}
                            >
                              {/* Apprenant */}
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: '6px' }}>
                                <Avatar
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    background: `linear-gradient(135deg, ${getTone('blue').deep}, ${getTone('sky').light})`,
                                    mr: 1.5,
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    color: grey(0),
                                  }}
                                >
                                  {(insc.apprenant_prenom || '?').charAt(0).toUpperCase()}{(insc.apprenant_nom || '?').charAt(0).toUpperCase()}
                                </Avatar>
                                <Box>
                                  <Typography sx={{ fontWeight: 700, fontSize: '13px', color: 'text.primary' }}>
                                    {insc.apprenant_prenom} {insc.apprenant_nom}
                                  </Typography>
                                  {insc.apprenant_profession && (
                                    <Typography sx={{ fontSize: '10px', color: 'text.secondary', fontStyle: 'italic' }}>
                                      {insc.apprenant_profession}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>

                              {/* Formation */}
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', ml: '44px', mb: '4px' }}>
                                <Typography sx={{ fontSize: '11px', color: 'text.primary', lineHeight: 1.3, fontWeight: 500 }}>
                                  🎓 {insc.formation_nom || 'N/A'}
                                </Typography>
                              </Box>

                              {/* Session */}
                              <Box sx={{ display: 'flex', alignItems: 'center', ml: '44px' }}>
                                <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                                  📅 Session {insc.session_numero || 'N/A'}
                                  {insc.session_debut && (
                                    <> · {new Date(insc.session_debut).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                    {insc.session_fin && (
                                      <> → {new Date(insc.session_fin).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</>
                                    )}</>
                                  )}
                                </Typography>
                                {insc.prix > 0 && (
                                  <Typography sx={{ ml: 'auto', fontSize: '12px', color: 'primary.main', fontWeight: 700 }}>
                                    <AnimatedNumber value={insc.prix} suffix=" €" />
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          ))
                        )}
                      </Box>
                    </Box>
                  </Fade>
                </Card>
              </Grid>

              {/* Card 2: Aujourd'hui */}
              <Grid
                item
                md={3}
                sm={6}
                xs={12}
              >
                <Card
                  component={motion.div}
                  whileHover={{ y: -6, scale: 1.015 }}
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    p: 3,
                    height: '100%',
                    backgroundImage: greenStyles.bg,
                    border: greenStyles.border,
                    borderRadius: '16px',
                    boxShadow: greenStyles.shadow,
                    overflow: 'hidden',
                    position: 'relative',
                    minHeight: '200px',
                    transition: 'box-shadow 0.3s ease',
                  }}
                >
                  <Box sx={{ zIndex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                    <Typography
                      color={greenStyles.text}
                      variant="overline"
                      sx={{ fontWeight: 700, letterSpacing: '0.8px', opacity: 0.85 }}
                    >
                      Aujourd&apos;hui
                    </Typography>
                    
                    {!loading ? (
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, color: greenStyles.text, mb: 0.5 }}
                      >
                        <AnimatedNumber value={data.gap.today} suffix=" €" />
                      </Typography>
                    ) : (
                      <Skeleton variant="text" width={140} height={40} animation="wave" />
                    )}

                    {!loading ? (
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: greenStyles.subtext, mb: 1, display: 'block' }}
                      >
                        dont décalés <AnimatedNumber value={data.gap.differenceToday} suffix=" €" />
                      </Typography>
                    ) : (
                      <Skeleton variant="text" width={120} height={16} animation="wave" />
                    )}

                    <Typography
                      color={greenStyles.text}
                      variant="overline"
                      sx={{ fontWeight: 700, letterSpacing: '0.8px', opacity: 0.7, mt: 0.5 }}
                    >
                      Hier
                    </Typography>
                    {!loading ? (
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: greenStyles.text }}
                      >
                        <AnimatedNumber value={data.gap.yesterday} suffix=" €" />
                      </Typography>
                    ) : (
                      <Skeleton variant="text" width={100} height={24} animation="wave" />
                    )}

                    {!loading ? (
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: greenStyles.subtext }}
                      >
                        dont décalés <AnimatedNumber value={data.gap.differenceYesterday} suffix=" €" />
                      </Typography>
                    ) : (
                      <Skeleton variant="text" width={120} height={16} animation="wave" />
                    )}
                  </Box>
                  <Box sx={{ zIndex: 1, width: 100, height: 50, opacity: 0.85, ml: 1 }}>
                    {!loading ? (
                      <ChartLine />
                    ) : (
                      <Skeleton variant="rectangular" width={100} height={50} animation="wave" />
                    )}
                  </Box>
                </Card>
              </Grid>

              {/* Card 3: Total */}
              <Grid
                item
                md={3}
                sm={6}
                xs={12}
              >
                <Card
                  component={motion.div}
                  whileHover={{ y: -6, scale: 1.015 }}
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    p: 3,
                    height: '100%',
                    backgroundImage: goldStyles.bg,
                    border: goldStyles.border,
                    borderRadius: '16px',
                    boxShadow: goldStyles.shadow,
                    overflow: 'hidden',
                    position: 'relative',
                    minHeight: '200px',
                    transition: 'box-shadow 0.3s ease',
                  }}
                >
                  <Box sx={{ zIndex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                    <Typography
                      color={goldStyles.text}
                      variant="overline"
                      sx={{ fontWeight: 700, letterSpacing: '0.8px', opacity: 0.85 }}
                    >
                      Total
                    </Typography>
                    
                    {!loading ? (
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, color: goldStyles.text, mb: 1 }}
                      >
                        <AnimatedNumber value={data.cost.actualYear} suffix=" €" />
                      </Typography>
                    ) : (
                      <Skeleton variant="text" width={140} height={40} animation="wave" />
                    )}
                  </Box>
                  <Box sx={{ zIndex: 1, width: 100, height: 50, opacity: 0.85, ml: 1 }}>
                    {!loading ? (
                      <ChartLine />
                    ) : (
                      <Skeleton variant="rectangular" width={100} height={50} animation="wave" />
                    )}
                  </Box>
                </Card>
              </Grid>

              {/* Card 4: Dernière Semaine */}
              <Grid
                item
                md={3}
                sm={6}
                xs={12}
              >
                <Card
                  component={motion.div}
                  whileHover={{ y: -6, scale: 1.015 }}
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    p: 3,
                    height: '100%',
                    backgroundImage: purpleStyles.bg,
                    border: purpleStyles.border,
                    borderRadius: '16px',
                    boxShadow: purpleStyles.shadow,
                    overflow: 'hidden',
                    position: 'relative',
                    minHeight: '200px',
                    transition: 'box-shadow 0.3s ease',
                  }}
                >
                  <Box sx={{ zIndex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                    <Typography
                      color={purpleStyles.text}
                      variant="overline"
                      sx={{ fontWeight: 700, letterSpacing: '0.8px', opacity: 0.85 }}
                    >
                      Derniere Semaine
                    </Typography>
                    
                    {!loading ? (
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, color: purpleStyles.text, mb: 1 }}
                      >
                        <AnimatedNumber value={data.profit.actualYearReals} suffix=" €" />
                      </Typography>
                    ) : (
                      <Skeleton variant="text" width={140} height={40} animation="wave" />
                    )}
                  </Box>
                  <Box sx={{ zIndex: 1, width: 100, height: 50, opacity: 0.85, ml: 1 }}>
                    {!loading ? (
                      <ChartLine />
                    ) : (
                      <Skeleton variant="rectangular" width={100} height={50} animation="wave" />
                    )}
                  </Box>
                </Card>
              </Grid>
            </Grid>
          ) : (
            user.access_level !== 0 && data.customer ? (
              <Grid container spacing={3}>
                {/* HERO — Mois en cours */}
                <Grid item xs={12}>
                  <HeroStatCard
                    loading={loading}
                    title="Mois en cours"
                    sales={data.customer.currentMonthSubscribers}
                    lastSales={data.customer.lastMonthSubscribers}
                    amount={data.customer.currentMonthCA}
                  />
                </Grid>

                {/* Stats secondaires */}
                <Grid item lg={3} md={6} sm={6} xs={12}>
                  <MiniStat
                    loading={loading}
                    title="Aujourd'hui"
                    accent={getTone('blue').base}
                    sales={data.customer.todaySubscribers}
                    lastSales={data.customer.yesterdaySubscribers}
                    amount={data.customer.todayCA}
                  />
                </Grid>
                <Grid item lg={3} md={6} sm={6} xs={12}>
                  <MiniStat
                    loading={loading}
                    title="Semaine en cours"
                    accent={getTone('emerald').base}
                    sales={data.customer.currentWeekSubscribers}
                    lastSales={data.customer.lastWeekSubscribers}
                    amount={data.customer.currentWeekCA}
                  />
                </Grid>
                <Grid item lg={3} md={6} sm={6} xs={12}>
                  <MiniStat
                    loading={loading}
                    title="Année en cours"
                    accent={getTone('violet').base}
                    sales={data.customer.currentYearSubscribers}
                    lastSales={data.customer.lastYearSubscribers}
                    amount={data.customer.currentYearCA}
                  />
                </Grid>
                {(data.customer.currentMonthPalier || data.customer.lastMonthPalier) && (
                  <Grid item lg={3} md={6} sm={6} xs={12}>
                    <Card
                      sx={{
                        p: 2.5,
                        height: '100%',
                        borderRadius: '18px',
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        borderLeft: `4px solid ${getTone('amber').base}`,
                        boxShadow: (theme) => (theme.palette.mode === 'light' ? `0 6px 18px -10px ${darkVeil(0.12)}` : `0 6px 18px -10px ${darkVeil(0.5)}`),
                      }}
                    >
                      <PrimeWidget
                        type="primes"
                        loading={loading}
                        title="Primes de ce mois"
                        salesAmount={data.customer.currentMonthCA}
                        lastSalesAmount={data.customer.lastMonthCA}
                        palierPercentage={data.customer.currentMonthPalier.percent}
                        lastMonthPalierPercentage={data.customer.lastMonthPalier.percent}
                        palier={data.customer.currentMonthPalier}
                      />
                    </Card>
                  </Grid>
                )}
              </Grid>
            ) : null
          )}
        </>
      )}
    </>
  );
};

export default FinanceOverview;
